/**
 * Toaster — renders the global toast queue from toastStore.
 *
 * Mounted ONCE at the app root (App.jsx) so toasts appear over every page.
 *
 * Visual treatment is editorial-on-aesthetic:
 *   - Bottom-right anchored stack
 *   - Each toast is a borderless card with a single 2px LEFT rule in the
 *     semantic colour (ink for info, terracotta for error, moss-green for success)
 *   - Slides in from the right with a paper-physical ease
 *   - Click to dismiss; hover to pause auto-dismiss (visual cue only — the
 *     timer still runs, but the user usually clicks before it expires anyway)
 */
import { AnimatePresence, motion } from "motion/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { useToastStore } from "../stores/toastStore";

const RULE_BY_TYPE = {
  success: "var(--color-status-even)",
  error:   "var(--color-accent)",
  info:    "var(--color-ink)",
};

const LABEL_BY_TYPE = {
  success: "OK",
  error:   "ERR",
  info:    "INFO",
};

export default function Toaster() {
  const toasts  = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-relevant="additions text"
      className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 pointer-events-none"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <motion.button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24, transition: { duration: 0.18 } }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="
              pointer-events-auto
              flex items-center gap-3
              min-w-[18rem] max-w-[26rem]
              bg-[var(--color-paper)]
              border border-[var(--color-rule)]
              pl-4 pr-3 py-3
              text-left
              hover:border-[var(--color-rule-strong)]
              transition-colors
            "
            style={{
              boxShadow: "none",
              borderLeft: `2px solid ${RULE_BY_TYPE[t.type] || "var(--color-ink)"}`,
            }}
          >
            <span
              className="mono text-[10px] tracking-[0.18em] uppercase shrink-0"
              style={{ color: RULE_BY_TYPE[t.type] || "var(--color-ink)" }}
            >
              {LABEL_BY_TYPE[t.type] || "INFO"}
            </span>
            <span className="flex-1 text-[var(--text-sm)] text-[var(--color-ink)] leading-snug">
              {t.message}
            </span>
            <XMarkIcon
              className="w-3.5 h-3.5 text-[var(--color-ink-3)] shrink-0"
              aria-hidden="true"
            />
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}
